<template>
  <div class="stock-view-page stock-multi-note-receipt">
    <StockDocumentPageHeading
      :title="$t('STOCK.RECEIVING_SESSION.PAGE_TITLE')"
      :code="isCreateMode ? '' : headingCode"
      :new-label="isCreateMode ? $t('STOCK.RECEIVING_SESSION.NEW') : ''"
    />

    <v-card v-if="isCreateMode" :loading="isProcessing">
      <v-card-text>
        <p class="mb-4">{{ $t("STOCK.RECEIVING_SESSION.START_HINT") }}</p>
        <v-autocomplete
          v-model="selectedDeliveryNoteIds"
          :items="deliveryNoteOptions"
          :loading="loadingDeliveryNotes"
          :search-input.sync="deliveryNoteSearch"
          item-text="label"
          item-value="id"
          :label="$t('STOCK.RECEIVING_SESSION.SELECT_DNS')"
          multiple
          chips
          deletable-chips
          small-chips
          outlined
          dense
          hide-details="auto"
          class="mb-4"
        />
        <v-btn
          color="primary"
          :disabled="selectedDeliveryNoteIds.length < 2"
          :loading="isProcessing"
          @click="startSession"
        >
          {{ $t("STOCK.RECEIVING_SESSION.START_SESSION") }}
        </v-btn>
      </v-card-text>
    </v-card>

    <v-card v-else :loading="isProcessing" :disabled="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#main">{{ headingCode || $t("STOCK.RECEIVING_SESSION.SESSION") }}</v-tab>
        <v-spacer />
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-chip v-if="sessionStatus" class="mr-2" :color="statusColor" small dark>
            {{ sessionStatus }}
          </v-chip>
          <v-btn v-if="canFinalize" class="mr-2 success" small @click="finalizeSession">
            {{ $t("STOCK.RECEIVING_SESSION.FINALIZE_SESSION") }}
          </v-btn>
          <v-btn v-if="canDiscard" class="mr-2 error" small @click="discardSession">
            {{ $t("STOCK.DOCUMENTS.DISCARD") }}
          </v-btn>
          <v-btn v-if="canSave" class="mr-2 success" small @click="save">
            {{ $t("GENERAL.LABELS.SAVE") }}
          </v-btn>
        </div>
      </v-tabs>

      <v-tabs-items v-model="tab">
        <v-tab-item value="main">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="3">
                  <v-text-field
                    :value="organizationLabel"
                    :label="$t('STOCK.WAREHOUSES.ORGANIZATION')"
                    readonly
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-text-field
                    :value="warehouseLabel"
                    :label="$t('STOCK.RECEIPTS.WAREHOUSE')"
                    readonly
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <DatePicker
                    v-model="actionDate"
                    :label="$t('STOCK.RECEIPTS.DATE') + '*'"
                    :disabled="!canSave"
                    without-hint
                  />
                </v-col>
              </v-row>

              <v-alert type="info" dense text class="mb-4">
                {{ $t("STOCK.RECEIVING_SESSION.WORKFLOW_HINT") }}
              </v-alert>

              <v-card
                v-for="block in blocks"
                :key="block.id"
                outlined
                class="mb-6"
              >
                <v-card-title class="subtitle-1 py-3">
                  <router-link
                    v-if="block.delivery_note"
                    :to="{ name: 'stock-delivery-notes-view', params: { id: block.delivery_note.id } }"
                  >
                    {{ block.delivery_note.code }}
                  </router-link>
                  <span v-else>{{ block.code }}</span>
                  <span v-if="block.supplierLabel" class="ml-2 grey--text text-body-2">
                    {{ block.supplierLabel }}
                  </span>
                  <v-spacer />
                  <span class="text-body-2 grey--text">{{ block.code }}</span>
                </v-card-title>
                <v-card-text>
                  <v-text-field
                    v-model="block.goods_receipt_no"
                    :label="$t('STOCK.RECEIPTS.GOODS_RECEIPT_NO')"
                    :disabled="!isBlockEditable(block)"
                    outlined
                    dense
                    clearable
                    maxlength="64"
                    hide-details
                    class="mb-3"
                    style="max-width: 280px"
                  />
                  <div class="stock-document-lines-wrap">
                    <v-simple-table dense class="stock-document-lines stock-document-lines--compact">
                      <thead>
                        <tr>
                          <th
                            v-for="col in lineTableColumns(block)"
                            :key="`${block.id}-${col.key}`"
                            :class="col.thClass"
                          >
                            {{ col.label }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <template v-for="(line, index) in block.lines">
                          <tr
                            :key="`${block.id}-line-${index}-${line.stock_product_id}`"
                            :class="{
                              'stock-document-lines__row--expanded': line._detailsOpen,
                              'stock-document-lines__row--blocked': line.quality_status === 'blocked',
                              'stock-document-lines__row--lot-error': lineMismatches(block, line),
                            }"
                          >
                            <td class="stock-document-lines__sticky stock-document-lines__sticky--index">
                              {{ index + 1 }}
                            </td>
                            <td class="stock-document-lines__sticky stock-document-lines__sticky--product stock-document-lines__product">
                              <div class="font-weight-medium">{{ lineProductLabel(line) }}</div>
                              <div class="text-caption grey--text">{{ lineProductMeta(line) }}</div>
                              <div
                                v-if="lineMismatches(block, line)"
                                class="text-caption warning--text"
                              >
                                {{ mismatchHint(block, line) }}
                              </div>
                            </td>
                            <td class="stock-document-lines__col-qty">
                              <StockLineSyncedQtyFields
                                :line="line"
                                :show-packaging="false"
                                :disabled="!isBlockEditable(block)"
                                :error="lineMismatches(block, line)"
                                :kg-hint="lineUnitCode(line) || 'kg'"
                                @focus="focusSelectAllInput"
                                @quantity-changed="onLineQtyOrUnitInput(line, lineQuantityKey)"
                              />
                              <StockLineField
                                v-model.number="line.gross_weight_kg"
                                type="number"
                                class="mt-1"
                                :hint="$t('STOCK.COMMON.GROSS_WEIGHT_KG')"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                              />
                            </td>
                            <td class="stock-document-lines__col-money">
                              <StockLineField
                                v-model.number="line.unit_cost_net"
                                type="number"
                                :hint="pricePerUnitHint(line)"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                                @input="onUnitCostInput(line, lineQuantityKey)"
                              />
                              <StockLineField
                                v-if="lineHasSecondaryUnit(line)"
                                :value="line.secondary_unit_price"
                                type="number"
                                class="mt-1"
                                :hint="pricePerUnitHint(line, secondaryUnitCode(line))"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                                @input="onSecondaryUnitPriceInput(line, lineQuantityKey, $event)"
                              />
                              <StockLineField
                                v-model.number="line.line_total_net"
                                type="number"
                                class="mt-1"
                                :hint="$t('STOCK.RECEIPTS.LINE_TOTAL_SHORT')"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                                @input="onLineTotalWithSecondaryPrice(line, lineQuantityKey)"
                              />
                            </td>
                            <td class="stock-document-lines__col-lot">
                              <StockLineField
                                v-model="line.lot_number"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                              />
                              <StockLineField
                                v-model="line.expiry_date"
                                type="date"
                                class="mt-1"
                                :hint="$t('STOCK.RECEIPTS.EXPIRY')"
                                :disabled="!isBlockEditable(block)"
                                @focus="focusSelectAllInput"
                              />
                            </td>
                            <td class="stock-document-lines__col-quality">
                              <v-select
                                v-model="line.quality_status"
                                :items="qualityStatusOptions"
                                item-value="value"
                                item-text="text"
                                dense
                                hide-details
                                :disabled="!isBlockEditable(block)"
                                @change="onQualityStatusChange(line)"
                              />
                            </td>
                            <td class="stock-document-lines__actions text-nowrap">
                              <v-btn
                                icon
                                small
                                :title="line._detailsOpen ? $t('STOCK.RECEIPTS.HIDE_DETAILS') : $t('STOCK.RECEIPTS.MORE_DETAILS')"
                                @click="toggleLineDetails(line)"
                              >
                                <v-icon small>
                                  {{ line._detailsOpen ? "mdi-chevron-up" : "mdi-chevron-down" }}
                                </v-icon>
                              </v-btn>
                              <v-btn
                                v-if="isBlockEditable(block) && line.stock_product_id"
                                icon
                                small
                                :title="$t('STOCK.RECEIPTS.DUPLICATE_PRODUCT_LINE')"
                                @click="duplicateLine(block, index)"
                              >
                                <v-icon small>mdi-plus</v-icon>
                              </v-btn>
                              <v-btn
                                v-if="isBlockEditable(block)"
                                icon
                                small
                                @click="removeLine(block, index)"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </td>
                          </tr>
                          <tr
                            v-if="line._detailsOpen"
                            :key="`${block.id}-detail-${index}`"
                            class="stock-document-lines__detail"
                          >
                            <td colspan="7">
                              <div class="stock-document-lines__detail-grid">
                                <div v-if="lineHasPackagingLevels(line)">
                                  <div class="stock-document-lines__detail-label">
                                    {{ $t("STOCK.COMMON.PACKAGING") }}
                                  </div>
                                  <StockLineSyncedQtyFields
                                    :line="line"
                                    :show-measures="false"
                                    :disabled="!isBlockEditable(block)"
                                    :error="lineMismatches(block, line)"
                                    @focus="focusSelectAllInput"
                                    @quantity-changed="onLineQtyOrUnitInput(line, lineQuantityKey)"
                                  />
                                </div>
                                <div>
                                  <div class="stock-document-lines__detail-label">
                                    {{ $t("STOCK.RECEIPTS.EXTERNAL_LOT") }}
                                  </div>
                                  <StockLineField
                                    v-model="line.external_lot_number"
                                    :disabled="!isBlockEditable(block)"
                                    @focus="focusSelectAllInput"
                                  />
                                </div>
                                <div>
                                  <div class="stock-document-lines__detail-label">
                                    {{ $t("STOCK.INVENTORY.PRODUCTION_DATE") }}
                                  </div>
                                  <StockLineField
                                    v-model="line.production_date"
                                    type="date"
                                    :disabled="!isBlockEditable(block)"
                                    @focus="focusSelectAllInput"
                                  />
                                </div>
                                <div>
                                  <div class="stock-document-lines__detail-label">
                                    {{ $t("STOCK.PURCHASE_ORDERS.VAT") }}
                                  </div>
                                  <StockLineField
                                    v-model.number="line.vat_rate"
                                    type="number"
                                    hint="%"
                                    narrow
                                    :disabled="!isBlockEditable(block)"
                                    @focus="focusSelectAllInput"
                                  />
                                </div>
                                <div>
                                  <div class="stock-document-lines__detail-label">
                                    {{ $t("STOCK.RECEIPTS.QUALITY_HOLD_REASON") }}
                                  </div>
                                  <StockLineField
                                    v-model="line.quality_hold_reason"
                                    :disabled="!isBlockEditable(block) || line.quality_status !== 'blocked'"
                                    @focus="focusSelectAllInput"
                                  />
                                </div>
                              </div>
                            </td>
                          </tr>
                        </template>
                      </tbody>
                    </v-simple-table>
                  </div>

                  <StockDocumentLineTotals
                    v-if="block.lines.length"
                    :lines="block.lines"
                    :currency-code="block.currency_code"
                  />

                  <div v-if="showsExtraCosts(block)" class="mt-4">
                    <div class="subtitle-2 mb-2">{{ $t("STOCK.RECEIPTS.EXTRA_COSTS") }}</div>
                    <v-simple-table dense>
                      <thead>
                        <tr>
                          <th>{{ $t("GENERAL.LABELS.DESCRIPTION") }}</th>
                          <th>{{ $t("STOCK.RECEIPTS.AMOUNT_NET") }}</th>
                          <th>{{ $t("STOCK.PURCHASE_ORDERS.VAT") }}</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(extra, extraIndex) in block.extraCosts" :key="extra._key">
                          <td>
                            <v-text-field
                              v-model="extra.description"
                              dense
                              hide-details
                              :disabled="!isBlockEditable(block)"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.amount_net"
                              type="number"
                              dense
                              hide-details
                              :disabled="!isBlockEditable(block)"
                            />
                          </td>
                          <td>
                            <v-text-field
                              v-model.number="extra.vat_rate"
                              type="number"
                              suffix="%"
                              dense
                              hide-details
                              :disabled="!isBlockEditable(block)"
                            />
                          </td>
                          <td>
                            <v-btn
                              v-if="isBlockEditable(block)"
                              icon
                              small
                              @click="block.extraCosts.splice(extraIndex, 1)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-simple-table>
                  </div>
                </v-card-text>
              </v-card>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import { debounce } from "lodash";
import StockDocumentPageHeading from "@/views/partials/stock/StockDocumentPageHeading.vue";
import StockLineField from "@/views/partials/stock/StockLineField.vue";
import StockDocumentLineTotals from "@/views/partials/stock/StockDocumentLineTotals.vue";
import StockLineSyncedQtyFields from "@/views/partials/stock/StockLineSyncedQtyFields.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import stockDocumentLinesMixin, {
  STOCK_SELECT_ALL_ON_FOCUS_ENABLED,
} from "@/views/partials/stock/stockDocumentLines.mixin";
import stockDocumentTotalsMixin from "@/views/partials/stock/stockDocumentTotals.mixin";
import { SHOW_SNACK } from "@/store/snack.module";
import { GET_STOCK_DELIVERY_NOTES } from "@/store/stock/stockDeliveryNotes.module";
import {
  UPDATE_STOCK_RECEIPT,
  DISCARD_STOCK_RECEIPT,
} from "@/store/stock/stockReceipts.module";
import {
  START_STOCK_RECEIVING_SESSION,
  GET_STOCK_RECEIVING_SESSION_CONTEXT,
  FINALIZE_STOCK_RECEIVING_SESSION,
} from "@/store/stock/stockReceivingSessions.module";

export default {
  name: "StockMultiPoReceiptSession",
  components: {
    StockDocumentPageHeading,
    StockLineField,
    StockDocumentLineTotals,
    StockLineSyncedQtyFields,
    DatePicker,
  },
  mixins: [stockDocumentLinesMixin, stockDocumentTotalsMixin],
  data() {
    return {
      tab: "main",
      isProcessing: false,
      loadingDeliveryNotes: false,
      deliveryNoteSearch: "",
      deliveryNoteOptions: [],
      selectedDeliveryNoteIds: [],
      actionDate: new Date().toISOString().slice(0, 10),
      blocks: [],
      lineQuantityKey: "quantity",
    };
  },
  computed: {
    isCreateMode() {
      return this.$route.name === "stock-receipts-multi-note-create";
    },
    sessionId() {
      return this.$route.params.id ? Number(this.$route.params.id) : null;
    },
    previewDocumentTotals() {
      return null;
    },
    qualityStatusOptions() {
      return [
        { value: "free", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_FREE") },
        { value: "blocked", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_BLOCKED") },
      ];
    },
    headingCode() {
      return this.blocks.map((block) => block.code).filter(Boolean).join(" · ");
    },
    organizationLabel() {
      return this.blocks[0]?.organizationLabel || "-";
    },
    warehouseLabel() {
      return this.blocks[0]?.warehouseLabel || "-";
    },
    sessionStatus() {
      const statuses = [...new Set(this.blocks.map((block) => block.status).filter(Boolean))];
      return statuses.join(", ");
    },
    statusColor() {
      if (this.blocks.every((block) => block.status === "finalized")) {
        return "success";
      }
      if (this.blocks.some((block) => block.status === "draft")) {
        return "grey";
      }
      return "primary";
    },
    canSave() {
      return this.blocks.some((block) => block.status === "draft")
        && this.hasPermissions("stock-receipts-edit");
    },
    canFinalize() {
      return this.canSave
        && this.blocks.some((block) => block.lines.length > 0)
        && this.hasPermissions("stock-receipts-finalize");
    },
    canDiscard() {
      return this.canSave && this.hasPermissions("stock-receipts-cancel");
    },
  },
  watch: {
    "$route.params.id"(id) {
      if (id && !this.isCreateMode) {
        this.loadSession();
      }
    },
    deliveryNoteSearch: debounce(function () {
      this.loadDeliveryNoteOptions();
    }, 300),
  },
  mounted() {
    if (this.isCreateMode) {
      this.loadDeliveryNoteOptions();
      return;
    }
    if (this.sessionId) {
      this.loadSession();
    }
  },
  methods: {
    focusSelectAllInput(event) {
      if (!STOCK_SELECT_ALL_ON_FOCUS_ENABLED) {
        return;
      }
      const input = event?.target;
      if (input && typeof input.select === "function") {
        input.select();
      }
    },
    formatNum(value) {
      const rounded = Math.round(Number(value || 0) * 10000) / 10000;
      return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    },
    async loadDeliveryNoteOptions() {
      this.loadingDeliveryNotes = true;
      try {
        const response = await this.$store.dispatch(GET_STOCK_DELIVERY_NOTES, {
          page: 1,
          itemsPerPage: 50,
          search: this.deliveryNoteSearch || "",
          direction: "inbound",
          status: "confirmed",
        });
        this.deliveryNoteOptions = (response.data || []).map((note) => ({
          id: note.id,
          label: `${note.code} · ${note.order_code || note.purchase_order?.code || ""} · ${note.company?.label || note.company?.name || ""}`,
        }));
      } finally {
        this.loadingDeliveryNotes = false;
      }
    },
    async startSession() {
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(START_STOCK_RECEIVING_SESSION, {
          delivery_note_ids: this.selectedDeliveryNoteIds,
        });
        await this.$router.replace({
          name: "stock-receipts-multi-note-view",
          params: { id: data.id },
        });
        await this.loadSession();
      } catch (errors) {
        this.notifyError(errors);
      } finally {
        this.isProcessing = false;
      }
    },
    async loadSession(options = {}) {
      if (!options.keepProcessing) {
        this.isProcessing = true;
      }
      try {
        const context = await this.$store.dispatch(
          GET_STOCK_RECEIVING_SESSION_CONTEXT,
          this.sessionId
        );
        const receipts = context.receipts || [];
        this.blocks = receipts.map((receipt) => this.blockFromReceipt(receipt));
        this.actionDate = this.blocks[0]?.action_date || this.actionDate;
      } catch (errors) {
        if (options.keepProcessing) {
          throw errors;
        }
        this.notifyError(errors);
      } finally {
        if (!options.keepProcessing) {
          this.isProcessing = false;
        }
      }
    },
    blockFromReceipt(receipt) {
      return {
        id: receipt.id,
        code: receipt.code,
        status: receipt.status,
        action_date: receipt.action_date,
        goods_receipt_no: receipt.goods_receipt_no || "",
        organizationLabel: receipt.organization?.label || receipt.organization?.name || "",
        warehouseLabel: receipt.destination_warehouse?.label || receipt.destination_warehouse?.name || "",
        warehouseType: receipt.destination_warehouse?.type || "",
        actionTypeSlug: receipt.action_type?.slug || "",
        supplierLabel: receipt.source_company?.label || receipt.source_company?.name || "",
        delivery_note: receipt.delivery_note || null,
        default_vat_rate: receipt.default_vat_rate,
        currency_code: receipt.currency_code || "EUR",
        lines: (receipt.items || []).map((item) => ({
          ...this.mapLineFromApi(item, receipt, this.lineQuantityKey),
          _detailsOpen: false,
          lot_number: item.lot_number || "",
          external_lot_number: item.external_lot_number || "",
          pallet_number: item.pallet_number || "",
          production_date: item.production_date,
          expiry_date: item.expiry_date,
          quality_status: item.quality_status === "blocked" ? "blocked" : "free",
          quality_hold_reason: item.quality_hold_reason || "",
          purchase_order_item_id: item.purchase_order_item_id,
          stock_delivery_note_item_id: item.stock_delivery_note_item_id,
          stock_transport_order_item_id: item.stock_transport_order_item_id,
          stock_transport_order_stop_item_id: item.stock_transport_order_stop_item_id,
          gross_weight_kg: item.gross_weight_kg,
        })),
        extraCosts: (receipt.extra_costs || []).map((extra, index) => ({
          _key: `extra-${receipt.id}-${extra.id || index}`,
          extra_cost_type_id: extra.extra_cost_type_id,
          allocation_method_id: extra.allocation_method_id,
          description: extra.description || "",
          amount_net: Number(extra.amount_net || 0),
          vat_rate: extra.vat_rate,
          currency_code: extra.currency_code,
          supplier_company_id: extra.supplier_company?.id || null,
          document_no: extra.document_no,
          document_date: extra.document_date,
        })),
      };
    },
    noteProduct(block, productId) {
      return (block.delivery_note?.products || []).find(
        (product) => Number(product.stock_product_id) === Number(productId)
      );
    },
    blockQuantityForProduct(block, productId) {
      return block.lines
        .filter((line) => Number(line.stock_product_id) === Number(productId))
        .reduce((sum, line) => sum + Number(line.quantity || 0), 0);
    },
    isBlockEditable(block) {
      return this.canSave && block.status === "draft";
    },
    showsExtraCosts(block) {
      return block.extraCosts.length > 0;
    },
    lineTableColumns(block) {
      const currency = block?.currency_code;
      const costLabel = this.$t("STOCK.RECEIPTS.COST");
      return [
        { key: "index", thClass: "stock-document-lines__sticky stock-document-lines__sticky--index", label: "#" },
        { key: "product", thClass: "stock-document-lines__sticky stock-document-lines__sticky--product", label: this.$t("STOCK.PRODUCTS.PRODUCT") },
        { key: "qty", thClass: "stock-document-lines__col-qty", label: this.$t("STOCK.PURCHASE_ORDERS.QTY") },
        { key: "cost", thClass: "stock-document-lines__col-money", label: currency ? `${costLabel} · ${currency}` : costLabel },
        { key: "lot", thClass: "stock-document-lines__col-lot", label: this.$t("STOCK.RECEIPTS.LOT_NUMBER") },
        { key: "quality", thClass: "stock-document-lines__col-quality", label: this.$t("STOCK.RECEIPTS.QUALITY_STATUS") },
        { key: "actions", thClass: "", label: "" },
      ];
    },
    onQualityStatusChange(line) {
      if (!line || line.quality_status === "blocked") {
        if (line) {
          this.$set(line, "_detailsOpen", true);
        }
        return;
      }
      line.quality_hold_reason = "";
    },
    toggleLineDetails(line) {
      this.$set(line, "_detailsOpen", !line._detailsOpen);
    },
    lineMismatches(block, line) {
      if (!this.isBlockEditable(block) || !block.delivery_note || !line?.stock_product_id) {
        return false;
      }
      const noteProduct = this.noteProduct(block, line.stock_product_id);
      if (!noteProduct) {
        return true;
      }
      return Math.abs(this.blockQuantityForProduct(block, line.stock_product_id) - Number(noteProduct.quantity)) > 0.0001;
    },
    mismatchHint(block, line) {
      const noteProduct = this.noteProduct(block, line.stock_product_id);
      return this.$t("STOCK.COMMON.DELIVERY_NOTE_QTY_MISMATCH", {
        product: noteProduct?.display_name || this.lineProductLabel(line),
        note: block.delivery_note?.code || "",
        noteQty: this.formatNum(noteProduct ? noteProduct.quantity : 0),
        docQty: this.formatNum(this.blockQuantityForProduct(block, line.stock_product_id)),
        unit: noteProduct?.unit_code || this.lineUnitCode(line) || "kg",
      });
    },
    duplicateLine(block, index) {
      const line = block.lines[index];
      if (!line?.stock_product_id) {
        return;
      }
      const copy = {
        ...line,
        quantity: 1,
        secondary_quantity: null,
        packaging_quantities: {},
        gross_weight_kg: null,
        lot_number: "",
        external_lot_number: "",
        production_date: null,
        expiry_date: null,
        _detailsOpen: false,
      };
      delete copy.id;
      this.initializeLinePackagingQuantities(copy, line.stock_product);
      copy.line_total_net = this.computeLineNetFromUnit(copy, this.lineQuantityKey);
      block.lines.splice(index + 1, 0, copy);
    },
    removeLine(block, index) {
      block.lines.splice(index, 1);
    },
    blockPayload(block) {
      return {
        action_date: this.actionDate,
        goods_receipt_no: block.goods_receipt_no || null,
        items: block.lines
          .filter((line) => line.stock_product_id)
          .map((line, index) => ({
            line_no: index + 1,
            stock_product_id: line.stock_product_id,
            quantity: line.quantity,
            unit_id: line.unit_id,
            gross_weight_kg: line.gross_weight_kg,
            unit_cost_net: line.unit_cost_net,
            vat_rate: line.vat_rate ?? block.default_vat_rate,
            lot_number: line.lot_number,
            external_lot_number: line.external_lot_number || null,
            pallet_number: line.pallet_number || null,
            production_date: line.production_date,
            expiry_date: line.expiry_date,
            quality_status: line.quality_status === "blocked" ? "blocked" : "free",
            quality_hold_reason: line.quality_status === "blocked" ? line.quality_hold_reason || null : null,
            purchase_order_item_id: line.purchase_order_item_id,
            stock_delivery_note_item_id: line.stock_delivery_note_item_id,
            stock_transport_order_item_id: line.stock_transport_order_item_id,
            stock_transport_order_stop_item_id: line.stock_transport_order_stop_item_id,
          })),
        extra_costs: block.extraCosts.map((extra) => ({
          extra_cost_type_id: extra.extra_cost_type_id,
          allocation_method_id: extra.allocation_method_id,
          description: extra.description,
          amount_net: extra.amount_net,
          vat_rate: extra.vat_rate,
          currency_code: extra.currency_code,
          supplier_company_id: extra.supplier_company_id,
          document_no: extra.document_no,
          document_date: extra.document_date,
        })),
      };
    },
    validateBlocks() {
      for (const block of this.blocks) {
        const seen = new Set();
        for (const line of block.lines) {
          const productId = Number(line.stock_product_id);
          if (!productId || seen.has(productId) || !this.lineMismatches(block, line)) {
            continue;
          }
          seen.add(productId);
          this.notifyError({ message: this.mismatchHint(block, line) });
          return false;
        }
      }
      return true;
    },
    async save(options = {}) {
      if (!this.validateBlocks()) {
        return false;
      }
      this.isProcessing = true;
      try {
        for (const block of this.blocks.filter((row) => row.status === "draft")) {
          await this.$store.dispatch(UPDATE_STOCK_RECEIPT, {
            id: block.id,
            params: this.blockPayload(block),
          });
        }
        await this.loadSession({ keepProcessing: true });
        if (!options.quiet) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.SUCCESS_MESSAGE"),
            color: "success",
          });
        }
        return true;
      } catch (errors) {
        this.notifyError(errors);
        return false;
      } finally {
        this.isProcessing = false;
      }
    },
    async finalizeSession() {
      if (!this.validateBlocks()) {
        return;
      }
      const missingLot = this.blocks.some((block) =>
        block.lines.some((line) => line.stock_product_id && !String(line.lot_number || "").trim())
      );
      if (missingLot) {
        this.notifyError({ message: this.$t("STOCK.RECEIVING_SESSION.LOT_REQUIRED") });
        return;
      }
      const saved = await this.save({ quiet: true });
      if (!saved) {
        return;
      }
      this.isProcessing = true;
      try {
        const data = await this.$store.dispatch(FINALIZE_STOCK_RECEIVING_SESSION, this.sessionId);
        if (data?.id && Number(data.id) !== Number(this.sessionId)) {
          await this.$router.replace({
            name: "stock-receipts-multi-note-view",
            params: { id: data.id },
          });
          return;
        }
        await this.loadSession();
        this.$store.dispatch(SHOW_SNACK, {
          message: this.$t("STOCK.RECEIPTS.FINALIZED_MSG"),
          color: "success",
        });
      } catch (errors) {
        this.notifyError(errors);
      } finally {
        this.isProcessing = false;
      }
    },
    async discardSession() {
      if (!confirm(this.$t("STOCK.DOCUMENTS.CONFIRM_DISCARD"))) {
        return;
      }
      this.isProcessing = true;
      try {
        for (const block of this.blocks.filter((row) => row.status === "draft")) {
          await this.$store.dispatch(DISCARD_STOCK_RECEIPT, block.id);
        }
        this.$router.push({ name: "stock-movements" });
      } catch (errors) {
        this.notifyError(errors);
      } finally {
        this.isProcessing = false;
      }
    },
    notifyError(errors) {
      const message =
        (errors?.errors && Object.values(errors.errors).flat()[0]) ||
        errors?.message ||
        this.$t("GENERAL.UNEXPECTED_ERROR");
      this.$store.dispatch(SHOW_SNACK, { message, color: "error" });
    },
  },
};
</script>

<style scoped>
.stock-document-lines-wrap {
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}
.stock-document-lines {
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
.stock-document-lines th,
.stock-document-lines td {
  vertical-align: top !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
}
.stock-document-lines__row--lot-error {
  background: #fff8f8;
}
.stock-document-lines thead th {
  background: #f5f5f5;
  white-space: nowrap;
}
.stock-document-lines__sticky--index {
  position: sticky;
  left: 0;
  z-index: 2;
  width: 44px;
  min-width: 44px;
  max-width: 44px;
  background: #fff;
  text-align: center;
}
.stock-document-lines__sticky--product {
  position: sticky;
  left: 44px;
  z-index: 2;
  min-width: 280px;
  width: 32%;
  max-width: 360px;
  background: #fff;
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.14);
}
.stock-document-lines thead .stock-document-lines__sticky {
  z-index: 4;
  background: #f5f5f5;
}
.stock-document-lines__product .font-weight-medium {
  line-height: 1.25;
}
.stock-document-lines__col-qty {
  min-width: 128px;
  max-width: 160px;
  vertical-align: top;
}
.stock-document-lines__col-money {
  min-width: 140px;
  max-width: 168px;
}
.stock-document-lines__col-lot {
  min-width: 176px;
  max-width: 210px;
}
.stock-document-lines__col-quality {
  min-width: 120px;
  max-width: 148px;
}
.stock-document-lines__actions {
  white-space: nowrap;
  width: 1%;
}
.stock-document-lines__row--expanded .stock-document-lines__sticky,
.stock-document-lines__row--expanded td {
  background: #fafafa;
}
.stock-document-lines__row--blocked .stock-document-lines__col-quality {
  box-shadow: inset 3px 0 0 #fb8c00;
}
.stock-document-lines__detail td {
  background: #fafafa !important;
  padding: 12px 16px 16px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.stock-document-lines__detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
  gap: 12px 16px;
  max-width: 960px;
}
.stock-document-lines__detail-label {
  font-size: 11px;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.54);
  margin-bottom: 4px;
}
.stock-document-lines :deep(.stock-line-field__input) {
  width: 100%;
}
.stock-document-lines :deep(.stock-line-field__input .v-input__slot) {
  min-height: 36px !important;
}
</style>
